import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, RotateCcw, FileSpreadsheet, ChevronDown, ChevronUp, ArrowRight, Loader2 } from "lucide-react";

export default function BasePricelistUploader({ baseMeta, onMetaChange }) {
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file, { force = false } = {}) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Sélectionnez un fichier .xlsx (format Q-trimestre Fortinet).");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/pricelist/base/upload${force ? "?force=true" : ""}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
        }
      );
      setResult(r.data);
      const d = r.data.diff || {};
      toast.success(
        `Liste mise à jour — ${r.data.row_count.toLocaleString("fr-FR")} SKU · ` +
        `+${d.added_count} / -${d.removed_count} / ~${d.changed_count}`
      );
      onMetaChange?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Échec de l'import";
      // 409 = older effective date than the active pricelist → offer a force confirmation.
      if (status === 409 && !force) {
        const ok = window.confirm(
          `${detail}\n\nVoulez-vous forcer le remplacement malgré tout ?`
        );
        if (ok) {
          // Restart the upload with force=true. Re-enter the function — the
          // outer `setUploading(false)` in finally still runs first.
          setUploading(false);
          handleFile(file, { force: true });
          return;
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRestore = async () => {
    if (!window.confirm("Restaurer la liste de prix précédente (sauvegarde) ? La liste actuelle sera remplacée.")) return;
    setRestoring(true);
    try {
      const r = await api.post("/pricelist/base/restore-default");
      toast.success(`Restauré — ${r.data.row_count.toLocaleString("fr-FR")} SKU`);
      setResult(null);
      onMetaChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aucune sauvegarde disponible.");
    } finally {
      setRestoring(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-sm mb-3" data-testid="base-pricelist-uploader">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
        data-testid="base-pricelist-uploader-toggle"
      >
        <div className="flex items-center gap-3 text-left">
          <FileSpreadsheet className="h-4 w-4 text-[#D42B1E] shrink-0" />
          <div>
            <p className="text-sm font-medium text-zinc-900">Mettre à jour la liste de prix de référence</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Téléversez la nouvelle édition trimestrielle (.xlsx) — même structure que la price list Q2 2026.
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-100">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-sm py-7 px-4 text-center transition-colors ${
              uploading ? "border-zinc-200 bg-zinc-50" : "border-zinc-300 hover:border-[#D42B1E] hover:bg-red-50/30 cursor-pointer"
            }`}
            onClick={() => !uploading && fileRef.current?.click()}
            data-testid="base-pricelist-dropzone"
          >
            {uploading ? (
              <>
                <Loader2 className="h-6 w-6 text-[#D42B1E] animate-spin" />
                <p className="text-sm text-zinc-700">Validation, swap atomique et calcul du diff…</p>
              </>
            ) : (
              <>
                <Upload className="h-6 w-6 text-zinc-400" />
                <p className="text-sm text-zinc-700">
                  <span className="font-semibold">Glissez-déposez</span> ou cliquez pour sélectionner un fichier .xlsx
                </p>
                <p className="text-xs text-zinc-500">
                  Format attendu : onglet « DataSet » avec colonnes Comments / Identifier / Family / Product / Type / Item / SKU / Description / Price (≤ 25 Mo).
                </p>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="base-pricelist-file-input"
            />
          </div>

          {/* Restore button */}
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-zinc-500">
              Liste actuelle :{" "}
              <span className="font-mono text-zinc-700">
                {baseMeta?.filename || "—"}
              </span>
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestore}
              disabled={restoring}
              className="rounded-sm border-zinc-300"
              data-testid="base-pricelist-restore-button"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {restoring ? "Restauration…" : "Restaurer la précédente"}
            </Button>
          </div>

          {/* Diff result */}
          {result && (
            <div className="mt-5 border border-zinc-200 rounded-sm overflow-hidden" data-testid="base-pricelist-diff">
              <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <p className="text-sm font-semibold text-zinc-900">Différentiel détecté</p>
                <p className="text-xs text-zinc-500 font-mono">
                  Nouvelle édition : {result.new_filename}
                </p>
              </div>
              <div className="grid grid-cols-3 divide-x divide-zinc-200 text-center">
                <div className="px-4 py-4">
                  <p className="text-2xl font-bold text-green-700 tabular-nums">+{result.diff.added_count}</p>
                  <p className="text-xs text-zinc-600 mt-1 uppercase tracking-wide">SKU ajoutés</p>
                </div>
                <div className="px-4 py-4">
                  <p className="text-2xl font-bold text-zinc-700 tabular-nums">−{result.diff.removed_count}</p>
                  <p className="text-xs text-zinc-600 mt-1 uppercase tracking-wide">SKU retirés</p>
                </div>
                <div className="px-4 py-4">
                  <p className="text-2xl font-bold text-amber-700 tabular-nums">~{result.diff.changed_count}</p>
                  <p className="text-xs text-zinc-600 mt-1 uppercase tracking-wide">Prix modifiés</p>
                </div>
              </div>

              {/* Top changed prices */}
              {result.diff.changed_sample?.length > 0 && (
                <div className="border-t border-zinc-200">
                  <p className="px-4 py-2 text-[11px] uppercase tracking-wide text-zinc-600 font-semibold bg-zinc-50">
                    Top 25 plus grosses variations de prix
                  </p>
                  <div className="max-h-[260px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-zinc-100 sticky top-0">
                        <tr className="text-[11px] uppercase tracking-wide text-zinc-500">
                          <th className="text-left px-4 py-2 font-semibold">SKU</th>
                          <th className="text-left px-4 py-2 font-semibold">Famille</th>
                          <th className="text-right px-4 py-2 font-semibold">Avant ($)</th>
                          <th className="text-right px-4 py-2 font-semibold">Après ($)</th>
                          <th className="text-right px-4 py-2 font-semibold">Δ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {result.diff.changed_sample.map((c) => (
                          <tr key={c.sku} className="hover:bg-zinc-50">
                            <td className="px-4 py-2 font-mono text-xs">{c.sku}</td>
                            <td className="px-4 py-2 text-zinc-600 truncate max-w-[200px]">{c.family || "—"}</td>
                            <td className="px-4 py-2 text-right text-xs font-mono text-zinc-500">
                              {c.old_price.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                            </td>
                            <td className="px-4 py-2 text-right text-xs font-mono text-zinc-900">
                              {c.new_price.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span className={`inline-block px-2 py-0.5 text-[11px] rounded-sm font-mono font-semibold ${
                                c.direction === "up" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
                              }`}>
                                {c.direction === "up" ? "+" : ""}{c.delta_pct.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Added / removed sample */}
              {(result.diff.added_sample?.length > 0 || result.diff.removed_sample?.length > 0) && (
                <div className="grid grid-cols-2 divide-x divide-zinc-200 border-t border-zinc-200">
                  <div className="px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide text-green-700 font-semibold mb-2">
                      Ajoutés (échantillon)
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.diff.added_sample.slice(0, 12).map((s) => (
                        <span key={s} className="px-2 py-0.5 text-[11px] bg-green-50 text-green-800 rounded-sm font-mono">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide text-zinc-700 font-semibold mb-2">
                      Retirés (échantillon)
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {result.diff.removed_sample.slice(0, 12).map((s) => (
                        <span key={s} className="px-2 py-0.5 text-[11px] bg-zinc-100 text-zinc-700 rounded-sm font-mono">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
